import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { STICKY_NOTE_COLORS } from '../../utils/workflowBuilderUtils';

function getColorClass(colorId) {
  return STICKY_NOTE_COLORS.find((c) => c.id === colorId)?.className || STICKY_NOTE_COLORS[0].className;
}

function StickyNoteCard({ note, onChange, onDelete, readOnly }) {
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e) => {
    if (readOnly || e.target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    setDragging(true);
    dragOffset.current = { x: e.clientX - note.x, y: e.clientY - note.y };
  };

  useEffect(() => {
    if (!dragging) return undefined;

    const handleMove = (e) => {
      onChange({
        ...note,
        x: Math.max(0, e.clientX - dragOffset.current.x),
        y: Math.max(0, e.clientY - dragOffset.current.y),
        updated_at: new Date().toISOString(),
      });
    };
    const handleUp = () => setDragging(false);

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [dragging, note, onChange]);

  return (
    <div
      className={cn(
        'absolute w-48 rounded-md border shadow-md',
        getColorClass(note.color),
        dragging && 'cursor-grabbing',
      )}
      style={{ left: note.x, top: note.y }}
      data-testid={`sticky-note-${note.id}`}
      data-workflow-canvas-interactive
    >
      <div
        className="flex cursor-grab items-center justify-between border-b border-black/10 px-2 py-1"
        onPointerDown={handlePointerDown}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Note</span>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onDelete(note.id)}
            className="rounded p-0.5 text-slate-500 hover:bg-black/5"
            aria-label="Delete note"
          >
            <X size={12} />
          </button>
        )}
      </div>
      <textarea
        value={note.text}
        onChange={(e) =>
          onChange({
            ...note,
            text: e.target.value,
            updated_at: new Date().toISOString(),
          })
        }
        readOnly={readOnly}
        placeholder="Write a note..."
        className="min-h-[80px] w-full resize-none bg-transparent px-2 py-2 text-sm text-slate-800 outline-none"
      />
    </div>
  );
}

const WorkflowCanvasStickyNotes = ({
  notes = [],
  visible = true,
  readOnly = false,
  onNotesChange,
}) => {
  const updateNote = useCallback(
    (updated) => {
      onNotesChange(notes.map((n) => (n.id === updated.id ? updated : n)));
    },
    [notes, onNotesChange],
  );

  const deleteNote = useCallback(
    (id) => {
      onNotesChange(notes.filter((n) => n.id !== id));
    },
    [notes, onNotesChange],
  );

  if (!visible) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10" data-testid="workflow-canvas-notes">
      {notes.map((note) => (
        <div key={note.id} className="pointer-events-auto">
          <StickyNoteCard
            note={note}
            onChange={updateNote}
            onDelete={deleteNote}
            readOnly={readOnly}
          />
        </div>
      ))}
    </div>
  );
};

export default WorkflowCanvasStickyNotes;
